/**
 * ContentPlayerSplitLayout
 *
 * Resizable split layout used by all content-type players (QuestionSet, Speaking,
 * Essay, Dropdown, etc.) in horizontal split mode.
 *
 * Left panel: primary vertical scroll + visible scrollbar.
 * Right panel: flex column; inner content-type regions own overflow scroll.
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import { attachWheelScrollChain } from './splitPanelWheelScroll';

// ─── Types ───────────────────────────────────────────────────────────────────

export type SplitRatioPreset =
    | '50-50'
    | '60-40'
    | '40-60'
    | '70-30'
    | '30-70';

export interface ContentPlayerSplitLayoutProps {
    /** CSS class prefix for scoping, e.g. `"question-set-player-split"`. */
    prefix: string;
    /** Preset ratio like `'60-40'`.  Falls back to `'50-50'`. */
    splitRatio?: SplitRatioPreset | string;
    /** Left panel content (lesson / page content). */
    leftPanel: React.ReactNode;
    /** Right panel content (questions / prompts). */
    rightPanel: React.ReactNode;
    /** Extra content rendered below the split panels (e.g. inline nav). */
    footer?: React.ReactNode;
    /** Additional className on the outer container. */
    className?: string;
    /** Minimum panel width in pixels. Default `200`. */
    minPanelWidthPx?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Convert a preset ratio string to the initial left-panel percentage. */
export function splitRatioPresetToLeftPercent(
    ratio: SplitRatioPreset | string | undefined
): number {
    switch (ratio) {
        case '60-40':
            return 60;
        case '40-60':
            return 40;
        case '70-30':
            return 70;
        case '30-70':
            return 30;
        case '50-50':
        default:
            return 50;
    }
}

// ─── Component ───────────────────────────────────────────────────────────────

const MIN_PANEL_WIDTH_DEFAULT = 200;

export const ContentPlayerSplitLayout: React.FC<ContentPlayerSplitLayoutProps> =
    React.memo(function ContentPlayerSplitLayout({
        prefix,
        splitRatio,
        leftPanel,
        rightPanel,
        footer,
        className,
        minPanelWidthPx = MIN_PANEL_WIDTH_DEFAULT,
    }) {
        const initialLeftPercent = splitRatioPresetToLeftPercent(splitRatio);
        const [leftPercent, setLeftPercent] = React.useState(initialLeftPercent);
        const [isDragging, setIsDragging] = React.useState(false);
        const containerRef = React.useRef<HTMLDivElement>(null);
        const leftScrollRef = React.useRef<HTMLDivElement>(null);
        const rightScrollRef = React.useRef<HTMLDivElement>(null);

        React.useEffect(() => {
            setLeftPercent(splitRatioPresetToLeftPercent(splitRatio));
        }, [splitRatio]);

        React.useEffect(() => {
            const cleanups: Array<() => void> = [];
            if (leftScrollRef.current) {
                cleanups.push(attachWheelScrollChain(leftScrollRef.current));
            }
            if (rightScrollRef.current) {
                cleanups.push(attachWheelScrollChain(rightScrollRef.current));
            }
            return () => {
                for (const detach of cleanups) {
                    detach();
                }
            };
        }, []);

        const handlePointerDown = React.useCallback(
            (e: React.PointerEvent<HTMLDivElement>) => {
                e.preventDefault();
                setIsDragging(true);
            },
            []
        );

        React.useEffect(() => {
            if (!isDragging) return;

            const onMove = (e: PointerEvent) => {
                if (!containerRef.current) return;
                const rect = containerRef.current.getBoundingClientRect();
                const w = rect.width;
                if (w === 0) return;
                const x = e.clientX - rect.left;
                const minPct = (minPanelWidthPx / w) * 100;
                const maxPct = 100 - minPct;
                setLeftPercent(
                    Math.max(minPct, Math.min(maxPct, (x / w) * 100))
                );
            };

            const onUp = () => setIsDragging(false);

            document.addEventListener('pointermove', onMove);
            document.addEventListener('pointerup', onUp);
            document.addEventListener('pointercancel', onUp);
            window.addEventListener('blur', onUp);

            return () => {
                document.removeEventListener('pointermove', onMove);
                document.removeEventListener('pointerup', onUp);
                document.removeEventListener('pointercancel', onUp);
                window.removeEventListener('blur', onUp);
            };
        }, [isDragging, minPanelWidthPx]);

        return (
            <div
                className={cn(
                    `${prefix}-layout`,
                    'flex min-h-0 w-full min-w-0 flex-1 flex-col',
                    className
                )}
            >
                <div
                    ref={containerRef}
                    className={cn(
                        `${prefix}`,
                        'relative flex min-h-0 min-w-0 flex-1 items-stretch'
                    )}
                    style={{
                        userSelect: isDragging ? 'none' : undefined,
                    }}
                >
                    {isDragging ? (
                        <div
                            className={cn(
                                `${prefix}-drag-overlay`,
                                'pointer-events-auto absolute inset-0 z-50 cursor-col-resize'
                            )}
                            aria-hidden
                        />
                    ) : null}

                    <div
                        ref={leftScrollRef}
                        className={cn(
                            `${prefix}-left`,
                            'relative flex min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden overscroll-contain',
                            '[scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5',
                            '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/35',
                            '[&::-webkit-scrollbar-thumb:hover]:bg-muted-foreground/55',
                            '[&::-webkit-scrollbar-track]:bg-transparent'
                        )}
                        style={{
                            flex: `0 0 ${leftPercent}%`,
                            transition: isDragging
                                ? 'none'
                                : 'flex-basis 200ms ease-out',
                        }}
                    >
                        {leftPanel}
                    </div>

                    <div
                        className={cn(
                            `${prefix}-divider`,
                            'group flex w-2 shrink-0 cursor-col-resize items-center justify-center',
                            isDragging
                                ? 'bg-primary/30'
                                : 'bg-transparent hover:bg-muted-foreground/10',
                            'transition-colors duration-150'
                        )}
                        onPointerDown={handlePointerDown}
                    >
                        <div
                            className={cn(
                                `${prefix}-divider-handle`,
                                'h-10 w-0.5 rounded-full transition-colors duration-150',
                                isDragging
                                    ? 'bg-primary'
                                    : 'bg-muted-foreground/30 group-hover:bg-primary/70'
                            )}
                        />
                    </div>

                    <div
                        ref={rightScrollRef}
                        className={cn(
                            `${prefix}-right`,
                            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
                        )}
                    >
                        {rightPanel}
                    </div>
                </div>

                {footer}
            </div>
        );
    });
